import { Filter, Plus, Search, X } from 'lucide-react'

import { Button } from '../../../ui/Button/Button'
import { Skeleton } from '../../../ui/Skeleton/Skeleton'
import { StatePanel } from '../../../ui/StatePanel/StatePanel'
import type {
  AttentionFilter,
  Conversation,
  ConversationFilter,
  ConversationStateFilter,
  IntentFilter,
} from '../conversations-model'
import type { InteractionOperationalState } from '../interaction-operational-contracts'
import styles from '../ConversationsPage.module.css'
import { ConversationEmptyState } from './ConversationEmptyState'
import { ConversationRow } from './ConversationRow'

/**
 * How a search of the clinic's whole list stands. `off`: no search, or only
 * the loaded conversations are searched. `loading` and `error`: the matches
 * among conversations already read show meanwhile, marked as partial.
 */
export type ConversationSearchView = 'off' | 'loading' | 'ready' | 'error'

type ConversationSearch = {
  view: ConversationSearchView
  // Listed matches for the search text alone, before the other filters.
  matchCount: number
  olderFailed: boolean
  onRetry: () => void
}

// The read view's limit for search text (conversation_search.MAX_SEARCH_LENGTH).
const MAX_SEARCH_LENGTH = 100

const NO_SEARCH: ConversationSearch = {
  view: 'off',
  matchCount: 0,
  olderFailed: false,
  onRetry: () => undefined,
}

export function ConversationInbox({
  status,
  conversations,
  loadedCount,
  query,
  channelFilter,
  attentionFilter,
  intentFilter,
  stateFilter,
  operationalStates,
  operationalStatus,
  selectedPatientId,
  hasOlder,
  loadingOlder,
  search = NO_SEARCH,
  onSelect,
  onLoadOlder,
  onQueryChange,
  onChannelFilterChange,
  onAttentionFilterChange,
  onIntentFilterChange,
  onStateFilterChange,
  onStartConversation,
  backgroundInert = false,
}: {
  status: 'loading' | 'ready' | 'forbidden' | 'error'
  conversations: Conversation[]
  loadedCount: number
  query: string
  channelFilter: ConversationFilter
  attentionFilter: AttentionFilter
  intentFilter: IntentFilter
  stateFilter: ConversationStateFilter
  operationalStates: Record<string, InteractionOperationalState>
  operationalStatus: 'idle' | 'loading' | 'ready' | 'error'
  selectedPatientId: string | null
  hasOlder: boolean
  loadingOlder: boolean
  search?: ConversationSearch
  onSelect: (conversation: Conversation) => void
  onLoadOlder: () => void
  onQueryChange: (query: string) => void
  onChannelFilterChange: (filter: ConversationFilter) => void
  onAttentionFilterChange: (filter: AttentionFilter) => void
  onIntentFilterChange: (filter: IntentFilter) => void
  onStateFilterChange: (filter: ConversationStateFilter) => void
  onStartConversation?: () => void
  backgroundInert?: boolean
}) {
  const otherFiltersActive =
    channelFilter !== 'All' ||
    attentionFilter !== 'Everything' ||
    intentFilter !== 'Any open request' ||
    stateFilter !== 'Active'
  const filtersActive = query.trim().length > 0 || otherFiltersActive
  const searching = search.view !== 'off'
  const activeFilterChips: Array<{
    key: string
    label: string
    onRemove: () => void
  }> = []
  if (query.trim()) {
    activeFilterChips.push({
      key: 'query',
      label: `Search: ${query.trim()}`,
      onRemove: () => onQueryChange(''),
    })
  }
  if (channelFilter !== 'All') {
    activeFilterChips.push({
      key: 'channel',
      label: channelFilter,
      onRemove: () => onChannelFilterChange('All'),
    })
  }
  if (attentionFilter !== 'Everything') {
    activeFilterChips.push({
      key: 'attention',
      label: attentionFilter,
      onRemove: () => onAttentionFilterChange('Everything'),
    })
  }
  if (intentFilter !== 'Any open request') {
    activeFilterChips.push({
      key: 'intent',
      label: intentFilter,
      onRemove: () => onIntentFilterChange('Any open request'),
    })
  }
  if (stateFilter !== 'Active') {
    activeFilterChips.push({
      key: 'state',
      label: stateFilter,
      onRemove: () => onStateFilterChange('Active'),
    })
  }
  const inertProps = (backgroundInert ? { inert: '' } : {}) as {
    inert?: string
  }

  return (
    <section
      {...inertProps}
      className={styles.inbox}
      aria-label="Recent conversations"
      aria-hidden={backgroundInert || undefined}
    >
      <div className={styles.paneHeader}>
        <div className={styles.paneTitle}>
          <h2>Inbox</h2>
          {status === 'ready' ? (
            <span className={styles.count} aria-label={`${loadedCount} loaded`}>
              {loadedCount}
            </span>
          ) : null}
        </div>
        <div className={styles.inboxHeaderActions}>
          {onStartConversation ? (
            <Button
              type="button"
              className={styles.startConversationButton}
              onClick={onStartConversation}
            >
              <Plus size={15} aria-hidden="true" /> Start Conversation
            </Button>
          ) : null}
        </div>
      </div>
      {status === 'ready' ? (
        <div className={styles.filterBar}>
          <div className={styles.searchField}>
            <label className={styles.srOnly} htmlFor="conversation-search">
              Search conversations
            </label>
            <Search size={16} aria-hidden="true" />
            <input
              id="conversation-search"
              type="search"
              value={query}
              maxLength={MAX_SEARCH_LENGTH}
              placeholder="Name or phone"
              onChange={(event) => onQueryChange(event.target.value)}
            />
            {query ? (
              <button
                type="button"
                aria-label="Clear conversation search"
                onClick={() => onQueryChange('')}
              >
                <X size={14} aria-hidden="true" />
              </button>
            ) : null}
          </div>
          <div
            className={styles.channelTabs}
            role="group"
            aria-label="Filter by channel"
          >
            {(['All', 'SMS', 'Voice'] satisfies ConversationFilter[]).map(
              (channel) => (
                <button
                  key={channel}
                  type="button"
                  aria-pressed={channelFilter === channel}
                  onClick={() => onChannelFilterChange(channel)}
                >
                  {channel}
                </button>
              )
            )}
          </div>
          <div className={styles.filterControls}>
            <details className={styles.advancedFilters}>
              <summary
                aria-label="Conversation filters"
                className={
                  filtersActive ? styles.activeFilterButton : undefined
                }
              >
                <Filter size={14} aria-hidden="true" /> Filters
              </summary>
              <div className={styles.filterPopover}>
                <FilterGroup
                  label="Attention"
                  values={
                    [
                      'Everything',
                      'Needs Action',
                      'Needs Review',
                      'Unread',
                      'Starred',
                    ] satisfies AttentionFilter[]
                  }
                  selected={attentionFilter}
                  onChange={onAttentionFilterChange}
                />
                <FilterGroup
                  label="Open requests"
                  values={
                    [
                      'Any open request',
                      'Appointment requests',
                      'Callback requests',
                      'Cancellation requests',
                      'Reschedule requests',
                    ] satisfies IntentFilter[]
                  }
                  selected={intentFilter}
                  onChange={onIntentFilterChange}
                />
                <FilterGroup
                  label="State"
                  values={
                    ['Active', 'Reviewed'] satisfies ConversationStateFilter[]
                  }
                  selected={stateFilter}
                  onChange={onStateFilterChange}
                />
              </div>
            </details>
            {activeFilterChips.length > 0 ? (
              <div
                className={styles.activeFilterChips}
                aria-label="Active conversation filters"
              >
                {activeFilterChips.map((filter) => (
                  <button
                    key={filter.key}
                    type="button"
                    className={styles.activeFilterChip}
                    aria-label={`Remove ${filter.label} filter`}
                    onClick={filter.onRemove}
                  >
                    <span>{filter.label}</span>
                    <X size={12} aria-hidden="true" />
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          {searching ? (
            <div className={styles.searchScope}>
              <p className={styles.scopeNote} role="status">
                {searchScope(
                  search,
                  conversations.length,
                  otherFiltersActive,
                  hasOlder
                )}
              </p>
              {search.view === 'error' ? (
                <button type="button" onClick={search.onRetry}>
                  Try again
                </button>
              ) : null}
            </div>
          ) : filtersActive ? (
            <p className={styles.scopeNote}>
              Showing {conversations.length} of {loadedCount} loaded
              conversation{loadedCount === 1 ? '' : 's'}
            </p>
          ) : null}
        </div>
      ) : null}
      {status === 'ready' && operationalStatus === 'error' ? (
        <div className={styles.workflowUnavailable} role="status">
          Status indicators are temporarily unavailable and may be incomplete.
        </div>
      ) : null}
      <div className={styles.inboxScroll}>
        {status === 'loading' ? (
          <div className={styles.inboxSkeleton} role="status">
            <span className={styles.srOnly}>Loading conversations</span>
            {Array.from({ length: 6 }, (_, index) => (
              <div className={styles.skeletonRow} key={index}>
                <Skeleton width="2.7rem" height="2.7rem" />
                <span>
                  <Skeleton width="68%" height="0.75rem" />
                  <Skeleton width="92%" height="0.62rem" />
                </span>
              </div>
            ))}
          </div>
        ) : status === 'forbidden' ? (
          <StatePanel
            kind="forbidden"
            title="Conversations unavailable"
            description="You do not have permission to view this clinic's conversations."
          />
        ) : status === 'error' ? (
          <StatePanel
            kind="error"
            title="Conversations unavailable"
            description="The recent conversation list could not be loaded."
          />
        ) : searching && conversations.length === 0 ? (
          search.view === 'loading' ? (
            <div className={styles.inboxSkeleton}>
              {Array.from({ length: 3 }, (_, index) => (
                <div className={styles.skeletonRow} key={index}>
                  <Skeleton width="2.7rem" height="2.7rem" />
                  <span>
                    <Skeleton width="68%" height="0.75rem" />
                    <Skeleton width="92%" height="0.62rem" />
                  </span>
                </div>
              ))}
            </div>
          ) : search.view === 'error' ? (
            <StatePanel
              kind="error"
              title="Search unavailable"
              description="Conversations could not be searched right now. Try again."
            />
          ) : (
            <ConversationEmptyState
              title={emptyTitle(attentionFilter, intentFilter, query)}
              description={
                hasOlder
                  ? 'No matches in the results loaded so far. Load more results to keep searching older conversations.'
                  : otherFiltersActive
                    ? 'No conversation in this clinic matches this search and these filters.'
                    : 'No conversation in this clinic matches this name or phone number.'
              }
            />
          )
        ) : loadedCount === 0 ? (
          <ConversationEmptyState
            title="No recent conversations"
            description="Conversations with indexed recent activity will appear here."
          />
        ) : conversations.length === 0 ? (
          <ConversationEmptyState
            title={emptyTitle(attentionFilter, intentFilter, query)}
            description="Try a different search or filter. Filters apply only to conversations currently loaded in this workspace."
          />
        ) : (
          <ul className={styles.conversationList}>
            {conversations.map((conversation) => (
              <ConversationRow
                key={conversation.conversationId}
                conversation={conversation}
                selected={selectedPatientId === conversation.conversationId}
                operationalState={
                  conversation.aggregateId
                    ? operationalStates[conversation.aggregateId]
                    : undefined
                }
                onSelect={() => onSelect(conversation)}
              />
            ))}
          </ul>
        )}
      </div>
      {status === 'ready' && hasOlder ? (
        <div className={styles.loadMore}>
          {searching && search.olderFailed ? (
            <p className={styles.scopeNote} role="alert">
              More results could not be loaded. Try again.
            </p>
          ) : null}
          <Button
            variant="secondary"
            type="button"
            disabled={loadingOlder}
            onClick={onLoadOlder}
          >
            {loadingOlder
              ? 'Loading…'
              : searching
                ? 'Load more results'
                : 'Load older conversations'}
          </Button>
        </div>
      ) : null}
    </section>
  )
}

function FilterGroup<T extends string>({
  label,
  values,
  selected,
  onChange,
}: {
  label: string
  values: T[]
  selected: T
  onChange: (value: T) => void
}) {
  return (
    <fieldset>
      <legend>{label}</legend>
      {values.map((value) => (
        <label key={value}>
          <input
            type="radio"
            name={`conversation-filter-${label}`}
            checked={selected === value}
            onChange={() => onChange(value)}
          />
          <span>{value}</span>
        </label>
      ))}
    </fieldset>
  )
}

function searchScope(
  search: ConversationSearch,
  shown: number,
  otherFiltersActive: boolean,
  hasMore: boolean
) {
  if (search.view === 'loading') return 'Searching all conversations…'
  if (search.view === 'error') {
    return 'Search is unavailable. Showing matches from loaded conversations only.'
  }
  const matches = `${search.matchCount} matching conversation${search.matchCount === 1 ? '' : 's'}`
  const listed = otherFiltersActive ? `Showing ${shown} of ${matches}` : matches
  return hasMore ? `${listed} so far · load more for older matches` : listed
}

function emptyTitle(
  attention: AttentionFilter,
  intent: IntentFilter,
  query: string
) {
  if (query.trim()) return 'No results match these filters'
  if (attention === 'Unread') return 'No unread conversations'
  if (attention === 'Needs Review') return 'Nothing needs review'
  if (attention === 'Starred') return 'No starred conversations'
  if (attention === 'Needs Action') return 'No conversations need action'
  if (intent === 'Appointment requests') return 'No appointment requests'
  if (intent === 'Callback requests') return 'No callback requests'
  if (intent === 'Cancellation requests') return 'No cancellation requests'
  if (intent === 'Reschedule requests') return 'No reschedule requests'
  return 'No results match these filters'
}
